import React, { useState, useEffect, useRef } from 'react';

import classNames from 'classnames';
import { Field } from 'react-final-form';

import { RangeSlider } from '../../../components';

import css from './IntegerRangeFilter.module.css';

const RADIX = 10;
// Keys that change a range handle (RangeSlider Handle, role=slider); their keyup applies the change
// in a live popup, like a handle release.
const COMMIT_KEYS = [
  'ArrowLeft',
  'ArrowRight',
  'ArrowUp',
  'ArrowDown',
  'Home',
  'End',
  'PageUp',
  'PageDown',
];

//  Ensures that the given values object has both minimum and maximum values.
//  If `values` does not include `minValue` or `maxValue`, defaults are applied.
const resolveMinMaxValues = (values, defaultMax, defaultMin) => {
  const { maxValue, minValue } = values || {};
  return {
    maxValue: maxValue != null ? maxValue : defaultMax,
    minValue: minValue != null ? minValue : defaultMin,
  };
};

const isTooSmall = (x, limit) => x < limit;
const isTooBig = (x, limit) => x > limit;
const isValidMin = (minValue, minLimit, maxValue) => {
  return (
    Number.isInteger(minValue) && !isTooSmall(minValue, minLimit) && !isTooBig(minValue, maxValue)
  );
};
const isValidMax = (maxValue, maxLimit, minValue) => {
  return (
    Number.isInteger(maxValue) && !isTooBig(maxValue, maxLimit) && !isTooSmall(maxValue, minValue)
  );
};

const getValidHandles = (currentValues, inputValues, minLimit, maxLimit) => {
  const isMinUpdated = currentValues.minValue !== inputValues.minValue;
  const isMaxUpdated = currentValues.maxValue !== inputValues.maxValue;

  if (isMinUpdated && isMaxUpdated) {
    const { minValue, maxValue } = inputValues;
    const inRange = !(isTooSmall(minValue, minLimit) || isTooBig(maxValue, maxLimit));
    // If both values are changed, are valid integers, and are within bounds, return the new values.
    // The integer check prevents faulty-mode '' values from escaping into the handles array,
    // which would crash labelForRangeInput via new Decimal('').
    return Number.isInteger(minValue) && Number.isInteger(maxValue) && inRange && minValue <= maxValue
      ? [minValue, maxValue]
      : [minLimit, maxLimit];
  }

  const { minValue, maxValue } = inputValues;
  const validMin = !isMinUpdated
    ? currentValues.minValue
    : !Number.isInteger(minValue)
    ? minLimit
    : isTooSmall(minValue, minLimit)
    ? minLimit
    : isTooBig(minValue, maxValue)
    ? maxValue
    : minValue;
  const validMax = !isMaxUpdated
    ? currentValues.maxValue
    : !Number.isInteger(maxValue)
    ? maxLimit
    : isTooBig(maxValue, maxLimit)
    ? maxLimit
    : isTooSmall(maxValue, minValue)
    ? minValue
    : maxValue;

  return [validMin, validMax];
};

const RangeInput = props => {
  const {
    input,
    min: defaultMinValue,
    max: defaultMaxValue,
    step,
    isInSideBar,
    initialValues,
    intl,
    getLabelForRangeInput,
    getHandleName,
    formatHandleValue,
    onCommit,
  } = props;
  const { value: values = {}, onChange, name } = input;

  // Live popup (onCommit): a change is applied on handle release, input blur or Enter,
  // never while a handle is dragged.
  const changedSinceCommit = useRef(false);
  const latestValues = useRef(values);
  const isDragging = useRef(false);
  const changeValues = newValues => {
    changedSinceCommit.current = true;
    latestValues.current = newValues;
    onChange(newValues);
  };
  const commit = () => {
    if (onCommit && changedSinceCommit.current) {
      changedSinceCommit.current = false;
      onCommit({ [name]: latestValues.current });
    }
  };
  const commitRef = useRef(commit);
  commitRef.current = commit;

  useEffect(() => {
    if (!onCommit) {
      return undefined;
    }
    const handleRelease = () => {
      if (isDragging.current) {
        isDragging.current = false;
        commitRef.current();
      }
    };
    // Capture phase: the slider handle stops the propagation of its mouseup/touchend
    window.addEventListener('mouseup', handleRelease, true);
    window.addEventListener('touchend', handleRelease, true);
    return () => {
      window.removeEventListener('mouseup', handleRelease, true);
      window.removeEventListener('touchend', handleRelease, true);
    };
  }, [onCommit]);

  // Closing the popup (e.g. outside click) unmounts the field before the input's blur
  useEffect(() => () => commitRef.current(), []);

  const startDragging = () => {
    isDragging.current = true;
  };
  const handleSliderKeyUp = e => {
    if (COMMIT_KEYS.includes(e.key)) {
      commit();
    }
  };
  const handleInputKeyDown = e => {
    if (e.key === 'Enter' && onCommit) {
      e.preventDefault();
      commit();
    }
  };

  const currentValues = resolveMinMaxValues(initialValues[name], defaultMaxValue, defaultMinValue);
  const [fieldValues, setFieldValues] = useState(currentValues);

  useEffect(() => {
    setFieldValues(currentValues);
  }, [initialValues]);

  const handleMinValueChange = event => {
    const newValue = Number.parseInt(event.target.value, RADIX);

    // Faulty mode: new min value is not an integer
    if (!Number.isInteger(newValue)) {
      const newValues = { ...fieldValues, minValue: '' };
      setFieldValues(newValues);
      return;
    }

    // Faulty mode: new min value is greater than the current max value or
    //              new min value is less than the default min value
    if (newValue > fieldValues.maxValue || newValue < defaultMinValue) {
      const newValues = { ...fieldValues, minValue: newValue };
      setFieldValues(newValues);
      return;
    }

    const newValues =
      newValue < defaultMinValue
        ? { ...fieldValues, minValue: defaultMinValue }
        : { ...fieldValues, minValue: newValue };
    setFieldValues(newValues);
    changeValues(newValues);
  };

  const handleMaxValueChange = event => {
    const newValue = Number.parseInt(event.target.value, RADIX);

    // Faulty mode: new min value is not an integer
    if (!Number.isInteger(newValue)) {
      const newValues = { ...fieldValues, maxValue: '' };
      setFieldValues(newValues);
      return;
    }

    // Faulty mode: new max value is less than the current min value or
    //              new max value is greater than the default max value
    if (newValue < fieldValues.minValue || newValue > defaultMaxValue) {
      const newValues = { ...fieldValues, maxValue: newValue };
      setFieldValues(newValues);
      return;
    }

    const newValues =
      newValue > defaultMaxValue
        ? { ...fieldValues, maxValue: defaultMaxValue }
        : { ...fieldValues, maxValue: newValue };
    setFieldValues(newValues);
    changeValues(newValues);
  };

  const handleMinValueBlur = event => {
    const inputValue = Number.parseInt(event.target.value, RADIX);
    if (
      !Number.isInteger(inputValue) ||
      inputValue < defaultMinValue ||
      inputValue > values.maxValue
    ) {
      const newValues = { ...fieldValues, minValue: defaultMinValue };
      setFieldValues(newValues);
      changeValues(newValues);
    }
    commit();
  };
  const handleMaxValueBlur = event => {
    const inputValue = Number.parseInt(event.target.value, RADIX);
    if (
      !Number.isInteger(inputValue) ||
      inputValue < values.minValue ||
      inputValue > defaultMaxValue
    ) {
      const newValues = { ...fieldValues, maxValue: defaultMaxValue };
      setFieldValues(newValues);
      changeValues(newValues);
    }
    commit();
  };

  const handleSliderChange = updatedValue => {
    setFieldValues({ ...updatedValue });
    changeValues({ ...updatedValue });
  };

  const labelForRangeInput = getLabelForRangeInput
    ? getLabelForRangeInput
    : (value, handleName) =>
        intl.formatMessage(
          { id: 'IntegerRangeFilter.screenreader.rangeHandle' },
          { value, handle: handleName }
        );

  // Slider handles: a fixed name per handle, the value goes to aria-valuetext
  const nameForHandle = getHandleName
    ? getHandleName
    : handleName =>
        intl.formatMessage(
          { id: 'IntegerRangeFilter.screenreader.handleName' },
          { handle: handleName }
        );
  const valueTextForHandle = formatHandleValue ? formatHandleValue : value => `${value}`;
  const getHandleNames = handles => {
    const multipleHandles = handles.length > 1;
    return handles.map((h, idx) => {
      const handleName =
        multipleHandles && idx === 0
          ? 'min'
          : multipleHandles && idx === handles.length - 1
          ? 'max'
          : 'handle';
      return nameForHandle(handleName);
    });
  };

  const isMinInvalid = !isValidMin(fieldValues.minValue, defaultMinValue, fieldValues.maxValue);
  const isMaxInvalid = !isValidMax(fieldValues.maxValue, defaultMaxValue, fieldValues.minValue);
  const classes = isInSideBar ? css.formWrapper : null;

  const validHandles = getValidHandles(values, fieldValues, defaultMinValue, defaultMaxValue);

  // In faulty mode, fieldValues.minValue / fieldValues.maxValue can be '' (empty string).
  // Passing '' to labelForRangeInput → formatCurrencyMajorUnit → new Decimal('') throws a
  // DecimalError and crashes the render. Use the safe default for the aria-label only.
  const minValueForLabel = Number.isInteger(fieldValues.minValue) ? fieldValues.minValue : defaultMinValue;
  const maxValueForLabel = Number.isInteger(fieldValues.maxValue) ? fieldValues.maxValue : defaultMaxValue;

  return (
    <div className={classes}>
      <div className={classNames(css.contentWrapper, { [css.contentWrapperSidebar]: isInSideBar })}>
        <div className={css.inputsWrapper}>
          {!isInSideBar ? (
            <span className={css.labelPopup}>
              {intl.formatMessage({ id: 'IntegerRangeFilter.rangeInputsLabel' })}
            </span>
          ) : null}
          <input
            className={classNames(css.minValue, {
              [css.valueInSidebar]: isInSideBar,
              [css.invalidInput]: isMinInvalid,
            })}
            inputMode="numeric"
            pattern="\d*"
            name={`${name}_min`}
            min={defaultMinValue}
            max={defaultMaxValue}
            placeholder={defaultMinValue}
            value={fieldValues.minValue}
            onChange={handleMinValueChange}
            onBlur={handleMinValueBlur}
            onKeyDown={handleInputKeyDown}
            aria-label={labelForRangeInput(minValueForLabel, 'min')}
          ></input>
          <span className={css.valueSeparator}>-</span>
          <input
            className={classNames(css.maxValue, {
              [css.valueInSidebar]: isInSideBar,
              [css.invalidInput]: isMaxInvalid,
            })}
            inputMode="numeric"
            pattern="\d*"
            name={`${name}_max`}
            min={defaultMinValue}
            max={defaultMaxValue}
            placeholder={defaultMaxValue}
            value={fieldValues.maxValue}
            onChange={handleMaxValueChange}
            onBlur={handleMaxValueBlur}
            onKeyDown={handleInputKeyDown}
            aria-label={labelForRangeInput(maxValueForLabel, 'max')}
          ></input>
        </div>
      </div>
      <div
        className={css.sliderWrapper}
        onMouseDownCapture={startDragging}
        onTouchStartCapture={startDragging}
        onKeyUp={handleSliderKeyUp}
        onBlur={commit}
      >
        <RangeSlider
          min={defaultMinValue}
          max={defaultMaxValue}
          ariaLabels={getHandleNames(validHandles)}
          ariaValueTexts={validHandles.map(valueTextForHandle)}
          step={step}
          handles={validHandles}
          onChange={handles => {
            handleSliderChange({ minValue: handles[0], maxValue: handles[1] });
          }}
        />
      </div>
    </div>
  );
};

/**
 * FieldSelectIntegerRange component
 *
 * @component
 * @param {Object} props
 * @param {number} [props.min] - The minimum value
 * @param {number} [props.max] - The maximum value
 * @param {string} [props.name] - The name
 * @param {number} [props.step] - The step
 * @param {boolean} [props.isInSideBar] - Whether the filter is in the sidebar
 * @returns {JSX.Element}
 */
const FieldSelectIntegerRange = props => {
  const { max, min, name, step = 5, isInSideBar = false, ...rest } = props;
  return (
    <Field
      max={max}
      min={min}
      name={name}
      step={step}
      isInSideBar={isInSideBar}
      component={RangeInput}
      {...rest}
    />
  );
};

export default FieldSelectIntegerRange;
